import { useState, useEffect } from 'react';
import { Clock, Trash2, AlertTriangle, CheckCircle, XCircle, RefreshCw, Shield } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';


interface InactiveAccount {
  id: string;
  email: string;
  name: string;
  lastActive: string;
  warningSentAt: string;
  deletionDate: string;
  daysLeft: number;
}

export function AdminInactiveAccounts({ accessToken }: { accessToken: string }) {
  const adminModalZ = useAdminModalZIndex();
  const [accounts, setAccounts] = useState<InactiveAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkResults, setCheckResults] = useState<{
    warnings_sent: number;
    accounts_deleted: number;
  } | null>(null);

  // Confirm dialogs
  const [showCheckConfirm, setShowCheckConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [accountToCancel, setAccountToCancel] = useState<string | null>(null);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);

  useEffect(() => {
    fetchPendingDeletions();
  }, []);

  const fetchPendingDeletions = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/pending-deletion`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      } else {
        toast.error('Kunne ikke laste kontoer som snart slettes');
      }
    } catch (error) {
      devError('Error fetching pending deletions:', error);
      toast.error('Feil ved lasting av data');
    } finally {
      setLoading(false);
    }
  };

  const runInactiveCheck = async () => {
    setChecking(true);
    setCheckResults(null);

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/check-inactive-accounts`,
        {
          method: 'POST',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCheckResults({
          warnings_sent: data.warnings_sent || 0,
          accounts_deleted: data.accounts_deleted || 0,
        });

        if (data.warnings_sent > 0 || data.accounts_deleted > 0) {
          toast.success(
            `Sjekk fullført! ${data.warnings_sent} varsler sendt, ${data.accounts_deleted} kontoer slettet.`
          );
        } else {
          toast.success('Sjekk fullført! Ingen kontoer funnet.');
        }

        // Refresh the list
        await fetchPendingDeletions();
      } else {
        const errorData = await response.json();
        toast.error(`Feil: ${errorData.error || 'Ukjent feil'}`);
      }
    } catch (error) {
      devError('Error running inactive check:', error);
      toast.error('Feil ved kjøring av inaktiv-sjekk');
    } finally {
      setChecking(false);
      setShowCheckConfirm(false);
    }
  };

  const cancelDeletion = async () => {
    if (!accountToCancel) return;

    setCancellingDeletion(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/cancel-deletion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: accountToCancel }),
        }
      );

      if (response.ok) {
        toast.success('Sletting avbrutt! Kontoen er nå aktiv igjen.');
        await fetchPendingDeletions();
      } else {
        const errorData = await response.json();
        toast.error(`Feil: ${errorData.error || 'Ukjent feil'}`);
      }
    } catch (error) {
      devError('Error cancelling deletion:', error);
      toast.error('Feil ved avbryting av sletting');
    } finally {
      setCancellingDeletion(false);
      setShowCancelConfirm(false);
      setAccountToCancel(null);
    }
  };

  const handleCancelClick = (accountId: string) => {
    setAccountToCancel(accountId);
    setShowCancelConfirm(true);
  };

  // Filter accounts with < 7 days left
  const accountsSoonToDelete = accounts.filter(acc => acc.daysLeft <= 7);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Check Button */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl text-gray-800 font-semibold flex items-center gap-2">
              <Clock className="w-6 h-6 text-purple-600" />
              Inaktive kontoer (Auto-sletting)
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Kontoer som har fått varsel om sletting. De slettes automatisk etter 30 dager.
            </p>
          </div>
          <button
            onClick={() => setShowCheckConfirm(true)}
            disabled={checking}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {checking ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Sjekker...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Kjør inaktiv-sjekk nå
              </>
            )}
          </button>
        </div>

        {/* Check Results */}
        {checkResults && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-green-900 font-medium">
              ✅ Siste sjekk fullført:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-green-800">
              <li>• {checkResults.warnings_sent} varsler sendt (23 måneder inaktiv)</li>
              <li>• {checkResults.accounts_deleted} kontoer slettet (30 dager siden varsel)</li>
            </ul>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-yellow-100">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span className="text-gray-600">Varsel sendt</span>
          </div>
          <p className="text-3xl text-yellow-900">{accounts.length}</p>
          <p className="text-sm text-gray-500 mt-1">Totalt kontoer med varsel</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100">
          <div className="flex items-center gap-3 mb-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <span className="text-gray-600">Slettes innen 7 dager</span>
          </div>
          <p className="text-3xl text-red-900">{accountsSoonToDelete.length}</p>
          <p className="text-sm text-gray-500 mt-1">Krever oppmerksomhet</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <span className="text-gray-600">Neste sjekk</span>
          </div>
          <p className="text-lg text-blue-900 mt-1">Manuell knapp</p>
          <p className="text-sm text-gray-500 mt-1">Du bestemmer når</p>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <h3 className="text-blue-900 font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Slik fungerer auto-sletting:
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">1.</span>
            <span>Kontoer som har vært inaktive i <strong>23 måneder</strong> får automatisk e-postvarsel</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">2.</span>
            <span>E-posten varsler at kontoen slettes om <strong>30 dager</strong> hvis de ikke logger inn</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">3.</span>
            <span>Hvis brukeren logger inn, nullstilles varselet og kontoen beholdes</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">4.</span>
            <span>Etter 30 dager uten innlogging slettes kontoen automatisk (GDPR-kompatibel)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">5.</span>
            <span>Du kan avbryte sletting manuelt ved å klikke "Avbryt sletting" nedenfor</span>
          </li>
        </ul>
      </div>

      {/* Accounts Soon to Delete (< 7 days) */}
      {accountsSoonToDelete.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-red-100 overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b border-red-100">
            <h3 className="text-red-900 font-semibold flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Kontoer som slettes innen 7 dager ({accountsSoonToDelete.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Bruker</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">E-post</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Sist aktiv</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Varsel sendt</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Dager igjen</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accountsSoonToDelete.map((account) => (
                  <tr key={account.id} className="hover:bg-red-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-gray-900 font-medium">{account.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">{account.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">
                        {new Date(account.lastActive).toLocaleDateString('nb-NO')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">
                        {new Date(account.warningSentAt).toLocaleDateString('nb-NO')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                          account.daysLeft <= 2
                            ? 'bg-red-100 text-red-900'
                            : account.daysLeft <= 5
                            ? 'bg-orange-100 text-orange-900'
                            : 'bg-yellow-100 text-yellow-900'
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        {account.daysLeft} {account.daysLeft === 1 ? 'dag' : 'dager'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleCancelClick(account.id)}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                      >
                        <XCircle className="w-4 h-4 inline mr-1" />
                        Avbryt sletting
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Accounts with Warning */}
      {accounts.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
          <div className="bg-purple-50 px-6 py-4 border-b border-purple-100">
            <h3 className="text-purple-900 font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alle kontoer med varsel ({accounts.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Bruker</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">E-post</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Sist aktiv</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Varsel sendt</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Slettes</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Dager igjen</th>
                  <th className="px-6 py-4 text-left text-gray-700 whitespace-nowrap">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{account.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">{account.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">
                        {new Date(account.lastActive).toLocaleDateString('nb-NO')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">
                        {new Date(account.warningSentAt).toLocaleDateString('nb-NO')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-sm">
                        {new Date(account.deletionDate).toLocaleDateString('nb-NO')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                          account.daysLeft <= 2
                            ? 'bg-red-100 text-red-900'
                            : account.daysLeft <= 7
                            ? 'bg-orange-100 text-orange-900'
                            : 'bg-yellow-100 text-yellow-900'
                        }`}
                      >
                        {account.daysLeft} {account.daysLeft === 1 ? 'dag' : 'dager'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleCancelClick(account.id)}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
                      >
                        Avbryt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl text-gray-800 font-semibold mb-2">
            Ingen kontoer venter på sletting
          </h3>
          <p className="text-gray-600">
            Det er ingen inaktive kontoer som har fått varsel om sletting.
          </p>
        </div>
      )}

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showCheckConfirm}
        onClose={() => setShowCheckConfirm(false)}
        onConfirm={runInactiveCheck}
        title="Kjør inaktiv-sjekk?"
        message="Dette vil sende varsler til kontoer som har vært inaktive i 23 måneder, og slette kontoer hvor 30 dager har gått siden varsel. Er du sikker?"
        confirmText="Kjør sjekk"
        cancelText="Avbryt"
        variant="warning"
        loading={checking}
        loadingText="Kjører sjekk..."
        portalZIndex={adminModalZ}
      />

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setAccountToCancel(null);
        }}
        onConfirm={cancelDeletion}
        title="Avbryt sletting?"
        message="Dette vil nullstille varselet og sette kontoen som aktiv igjen. Brukeren vil ikke lenger bli slettet automatisk."
        confirmText="Avbryt sletting"
        cancelText="Lukk"
        variant="info"
        loading={cancellingDeletion}
        loadingText="Avbryter..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
